import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Eye, Share2 } from 'lucide-react';
import { TemplatePreview } from './TemplatePreview';

// Sample user data for template preview
const sampleUserData = {
  personal_info: {
    name: "John Doe",
    firstName: "John",
    lastName: "Doe",
    profession: "Software Engineer",
    email: "john.doe@example.com",
    phone: "+33 6 12 34 56 78",
    fullAddress: "Paris, France"
  },
  profil: {
    description: "Experienced software engineer with a passion for creating innovative solutions."
  },
  summary: "Software engineer with 5+ years of experience in web development.",
  formations: [
    {
      diploma: "Master in Computer Science",
      school: "University of Technology",
      location: "Paris",
      startDate: "2018-09-01",
      endDate: "2020-06-30",
      description: "Specialized in software engineering and AI."
    }
  ],
  professional_experiences: [
    {
      title: "Senior Software Engineer",
      company: "Tech Company",
      startDate: "2020-07-01",
      endDate: "2023-12-31",
      isCurrent: false,
      description: "Led development of web applications using React and Node.js."
    }
  ,
    {
      title: "Full Stack Developer",
      company: "Tech Company",
      startDate: "2020-07-01",
      endDate: "2024-12-31",
      isCurrent: false,
      description: "Led development of web applications using React and Node.js."
    }
  ],
  skills: [
    {
      category: "Programming",
      items: ["JavaScript", "Python", "Java"],
      levels: { "JavaScript": 5, "Python": 4, "Java": 3 }
    }
  ],
  languages: [
    { name: "English", level: "Fluent" },
    { name: "French", level: "Native" }
  ],
  interests: ["Travel", "Photography", "Technology"],
  qualities: ["Creative", "Detail-oriented", "Team player"],
  certificates: [
    {
      title: "AWS Certified Developer",
      organization: "Amazon Web Services",
      date: "2022-05-15",
      description: "Associate level certification for cloud development"
    }
  ],
  references: [
    {
      name: "Jane Smith",
      role: "Engineering Manager",
      company: "Previous Company",
      email: "jane.smith@example.com",
      phone: "+33 6 98 76 54 32"
    }
  ],
  theme_colors: {
    RightSectionColor: "#ea580c",
    BgColor: "#f3f4f6",
    RightTextColor: "#111827",
    LeftTextColor: "##6b7280"
  }
};

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: {
    id?: number;
    template_id?: string;
    component_name?: string;
    name: string;
    category: string;
    industry: string;
    downloads?: number;
    rating?: number;
    tags?: string[];
    createdAt?: string;
    status?: string;
  };
}

export const TemplateModal: React.FC<TemplateModalProps> = ({ 
  isOpen, 
  onClose, 
  template 
}) => {
  if (!isOpen) return null;

  // Garantir qu'on a toujours un string pour templateName
  const templateName: string = template.component_name || template.name || template.template_id || 'Unknown';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{template.name}</h2>
              <div className="flex items-center gap-4 mt-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {template.category}
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  {template.industry}
                </span>
                    <div className="flex items-center gap-1">
                      <span className="text-yellow-500">★</span>
                      <span className="text-sm text-gray-600">{template.rating || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Download size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-600">{template.downloads?.toLocaleString() || '0'}</span>
                    </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex h-[calc(90vh-120px)]">
            {/* Template Preview */}
            <div className="flex-1 p-6 bg-gray-50 overflow-auto">
              <div className="flex justify-center items-center min-h-full">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden max-w-full max-h-full">
                  <div className="overflow-auto max-h-[calc(90vh-200px)]">
                    <TemplatePreview 
                      templateName={templateName} 
                      templateApiId={template.template_id}
                      userData={sampleUserData}
                      scale={1.5}
                      className="w-full h-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Template Details */}
            <div className="w-80 p-6 border-l border-gray-200 bg-white overflow-auto">
              <div className="space-y-6">
                {/* Tags */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {(template.tags || []).map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Template Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Créé le</span>
                      <p className="text-sm text-gray-900">
                        {template.createdAt ? new Date(template.createdAt).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Statut</span>
                      <p className="text-sm text-gray-900 capitalize">{template.status || 'active'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Téléchargements</span>
                      <p className="text-sm text-gray-900">{template.downloads?.toLocaleString() || '0'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Note moyenne</span>
                      <p className="text-sm text-gray-900">{template.rating || 'N/A'}/5</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Actions</h3>
                  <div className="space-y-3">
                    <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Eye size={16} />
                      Prévisualiser
                    </button>
                    <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                      <Download size={16} />
                      Télécharger
                    </button>
                    <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                      <Share2 size={16} />
                      Partager
                    </button>
                  </div>
                </div>

                {/* Template Features */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Caractéristiques</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Design responsive
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Optimisé pour l'impression
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Compatible PDF
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Personnalisable
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

